import { builtInCertificateTemplate } from "../certificates/certificateTemplates";
import { COURSE_LENGTH } from "./course";
import type { CertificateToRender } from "../../utils/renderCertificateFiles";
import type { AuthUser } from "../../types/authUser";

/**
 * The certificate for finishing Noticeboard Fundamentals: the default
 * certificate template (Champion) with the Organization's name and logo, made
 * out to the Team member, or to the Organization on an admin's login.
 */

/** Who the certificate is made out to: a Team member by name, an admin login by its Organization. */
export const courseCertificateName = (user: AuthUser | null | undefined): string => {
  if (!user) return "";
  const name = user.userType === "team" ? user.displayName || user.fullName : user.schoolName;
  return String(name ?? "").trim();
};

/** A short, stable code for one signed-in Team member, so their ID is the same on every download. */
const memberCode = (memberId: string): string => {
  let hash = 0;
  for (const char of memberId) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return hash.toString(36).toUpperCase().padStart(6, "0").slice(-6);
};

/** "NBF-2026-K3P9QZ": the year finished and the Team member's code. */
export const courseCertificateId = (memberId: string, finishedOn: string): string =>
  `NBF-${finishedOn.slice(0, 4)}-${memberCode(memberId)}`;

export const courseCertificate = ({
  name,
  memberId,
  finishedOn,
  organisation,
}: {
  name: string;
  memberId: string;
  /** "YYYY-MM-DD" */
  finishedOn: string;
  organisation: { name?: string; logoUrl?: string };
}): CertificateToRender => ({
  template: builtInCertificateTemplate(organisation),
  award: {
    certificateTitle: "Certificate of Completion",
    achievementName: "Noticeboard Fundamentals",
    description: `For completing all ${COURSE_LENGTH} lessons of the Noticeboard Fundamentals course.`,
    completionDate: finishedOn,
    signatures: [{ name: "Noticeboard", title: "Noticeboard Fundamentals" }],
  },
  recipient: { firstName: name, certificateId: courseCertificateId(memberId, finishedOn) },
});

/** "Noticeboard Fundamentals - Sam Smith.pdf" */
export const courseCertificateFileName = (name: string): string =>
  `Noticeboard Fundamentals${name ? ` - ${name.replace(/[\\/:*?"<>|]/g, "")}` : ""}.pdf`;
